import React, { useMemo, useState } from "react";
import { InvoiceStatus } from "@common/types/invoice";
import { useEntities } from "@hooks/useEntities";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import { useAddress } from "@api/entity.api";
import { useInvoices, useLastInvoiceNumber } from "@api/invoice.api";
import { useInvoiceTemplate } from "@api/invoiceTemplate.api";
import { usePreviewNativeInvoice } from "@api/native-invoice.api";
import { useLedgers } from "@api/ledger.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    findIncomeAccountLedgerId,
    findPrimaryLedgerId,
} from "@lib/accountOverrides";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { buildNativeInvoiceLineCodingByLineId } from "./buildNativeInvoiceLineCodingByLineId";
import { computeSuggestedInvoiceNumber } from "./computeSuggestedInvoiceNumber";
import { NativeInvoiceCreatePrefill } from "./nativeInvoiceFormTypes";
import {
    NativeInvoiceDraftHeader,
    NativeInvoiceDrawerMode,
} from "./NativeInvoiceDraftHeader";
import { NativeInvoiceFormWorkspace } from "./NativeInvoiceFormWorkspace";
import styles from "./NativeInvoiceDrawer.module.scss";

function resolveDrawerMode({
    isCreateTemplateMode,
    isTemplateMode,
    isEdit,
}: {
    isCreateTemplateMode: boolean;
    isTemplateMode: boolean;
    isEdit: boolean;
}): NativeInvoiceDrawerMode {
    if (isCreateTemplateMode) {
        return "createTemplate";
    }
    if (isTemplateMode) {
        return "template";
    }
    if (isEdit) {
        return "edit";
    }
    return "create";
}

function resolveIsFormReady({
    isEdit,
    isEditReady,
    templateRef,
    isTemplateReady,
    selectedEntity,
    isCreateReady,
}: {
    isEdit: boolean;
    isEditReady: boolean;
    templateRef: { templateId: string; entityId: number } | undefined;
    isTemplateReady: boolean;
    selectedEntity: { id: number } | undefined;
    isCreateReady: boolean;
}): boolean {
    if (isEdit) {
        return isEditReady;
    }
    if (templateRef) {
        return isTemplateReady && selectedEntity != null;
    }
    return isCreateReady;
}

interface Props {
    open?: boolean;
    onClose: () => void;
    initialInvoiceId?: string;
    // Create-mode only (ignored when initialInvoiceId is set): pre-fills the
    // draft with ad-hoc line items and other form fields instead of the blank
    // single-line default. See NativeInvoiceCreatePrefill.
    createPrefill?: NativeInvoiceCreatePrefill;
    applyTemplate?: { templateId: string; entityId: number };
    editTemplate?: { templateId: string; entityId: number };
    createTemplate?: boolean;
    // Loads an existing template's content to seed a brand-new create-template
    // session (drawerMode "createTemplate"), so the source template is never
    // mutated. See InvoiceTemplatesList's Duplicate action.
    duplicateTemplate?: { templateId: string; entityId: number };
    // Fires after an editTemplate session persisted its changes, just before it
    // closes. Lets a host invoice session (which opens this drawer stacked on
    // top of itself) pull the edited template back into its draft.
    onTemplateSaved?: () => void;
    // Opened on top of another invoice sheet: adds a scrim between the layers
    // so the sheet below visibly steps into the background.
    isStacked?: boolean;
    // Fires once the sheet has finished animating open or closed, so a stacked
    // host can keep this session mounted until its exit has played out.
    onAnimationEnd?: (open: boolean) => void;
}

export const NativeInvoiceDrawer: React.FC<Props> = ({
    open,
    onClose,
    initialInvoiceId,
    createPrefill,
    applyTemplate,
    editTemplate,
    createTemplate,
    duplicateTemplate,
    onTemplateSaved,
    isStacked,
    onAnimationEnd,
}) => {
    const { workspaceId } = useScope();
    const currentUser = useUser();
    const entities = useEntities({ onlyBusiness: true });
    const isEdit = Boolean(initialInvoiceId);
    const isCreateTemplateMode =
        Boolean(createTemplate) || Boolean(duplicateTemplate);
    const isTemplateMode = Boolean(editTemplate) || isCreateTemplateMode;
    const templateRef = editTemplate ?? applyTemplate ?? duplicateTemplate;
    const drawerMode = resolveDrawerMode({
        isCreateTemplateMode,
        isTemplateMode: Boolean(editTemplate),
        isEdit,
    });

    const invoiceQuery = useInvoices(
        {
            filters: initialInvoiceId ? { ids: [initialInvoiceId] } : undefined,
            page: 1,
            limit: 1,
        },
        { enabled: open && Boolean(initialInvoiceId) },
    );
    // The disabled create-mode query shares its cache key (no filters, page 1,
    // limit 1) with the workspace-records probe, so it can still surface the
    // workspace's latest invoice. Only trust the row in edit sessions, or a
    // create session inherits that invoice's status (PDF-only, no editor).
    const existingInvoice = initialInvoiceId
        ? invoiceQuery.data?.data?.[0]
        : undefined;
    const existingInvoiceEntityId = existingInvoice?.entityId ?? null;

    // User override wins; otherwise prefer the invoice's entity (edit) or the
    // first business entity (create). Derived — no effect to catch up when the
    // invoice row arrives.
    const [userSelectedEntityId, setUserSelectedEntityId] = useState<
        number | null
    >(
        createPrefill?.entityId ??
            applyTemplate?.entityId ??
            editTemplate?.entityId ??
            duplicateTemplate?.entityId ??
            null,
    );
    const selectedEntityId =
        userSelectedEntityId ??
        existingInvoiceEntityId ??
        entities[0]?.id ??
        null;

    const selectedEntity =
        entities.find((entity) => entity.id === selectedEntityId) ??
        entities[0];
    const entityId = selectedEntity?.id;

    const lastInvoiceNumberQuery = useLastInvoiceNumber({
        enabled: open && !initialInvoiceId && !isTemplateMode,
        entityId,
    });
    const entityAddressQuery = useAddress(entityId, {
        enabled: open && entityId != null && !isEdit && !templateRef,
    });
    const templateQuery = useInvoiceTemplate({
        entityId: templateRef?.entityId,
        templateId: templateRef?.templateId,
        enabled: open && Boolean(templateRef),
    });

    const ledgersQuery = useLedgers(entityId);
    const ledgers = ledgersQuery.data;
    const primaryAccrualLedgerId = findPrimaryLedgerId(
        ledgers,
        AccountingLedgerBasis.accruals,
    );
    const incomeAccountLedgerId = findIncomeAccountLedgerId(ledgers);
    // The form hydrates line coding once, on mount. Wait until ledgers resolve
    // so the income account (cash ledger when the entity has no accrual
    // ledger) is present for that first seed and the first save.
    const areLedgersResolved = ledgersQuery.isFetched || ledgersQuery.isError;

    // Always fetch against the invoice's owning entity — not the form's From
    // selection. Keying on the selected entity would create a fresh (disabled)
    // cache entry on entity switch and unmount the workspace into a spinner.
    const previewQuery = usePreviewNativeInvoice({
        workspaceId: workspaceId ?? undefined,
        entityId: existingInvoiceEntityId ?? undefined,
        invoiceId: initialInvoiceId,
        enabled:
            open &&
            Boolean(initialInvoiceId) &&
            existingInvoiceEntityId !== null,
    });

    // `undefined` while in flight; on error, still seed a first-number suggestion.
    // The form keeps the last defined value during entity-switch refetch gaps so
    // the field does not blank (and so this gate does not need to freeze it).
    const suggestedInvoiceNumber = computeSuggestedInvoiceNumber({
        isEdit,
        isError: lastInvoiceNumberQuery.isError,
        lastInvoiceNumber: lastInvoiceNumberQuery.data,
        entityName: selectedEntity?.name,
    });

    const existingCounterparty = existingInvoice?.counterparty;
    const initialCustomer = useMemo(
        () =>
            existingCounterparty
                ? {
                      id: existingCounterparty.id,
                      name: existingCounterparty.name,
                      email: existingCounterparty.email,
                      cc: existingCounterparty.cc,
                      bcc: existingCounterparty.bcc,
                      phoneNumber: existingCounterparty.phoneNumber,
                      street: existingCounterparty.street,
                      street2: existingCounterparty.street2,
                      city: existingCounterparty.city,
                      state: existingCounterparty.state,
                      postalCode: existingCounterparty.postalCode,
                      country: existingCounterparty.country,
                  }
                : null,
        [existingCounterparty],
    );
    const existingInvoiceLines = existingInvoice?.lines;
    const initialLineCodingByLineId = useMemo(
        () => buildNativeInvoiceLineCodingByLineId(existingInvoiceLines),
        [existingInvoiceLines],
    );
    // Preview stays keyed to the invoice entity above, so From switches keep
    // the successful cache entry and the form stays mounted. Create only needs
    // an entity — invoice # can arrive a moment later; the form derives it.
    // Do not gate on suggestedInvoiceNumber or entity-switch refetch gaps
    // would unmount the workspace and wipe in-progress edits.
    //
    // The address gate exists only to seed the *initial* From block without a
    // flash, so it must apply once. Latch it: once the create workspace has
    // opened we keep it mounted, because a From-entity switch re-keys
    // useAddress(entityId) and its isFetched drops to false while the new
    // entity's address is fetched — regating here would unmount the workspace
    // into a spinner and wipe in-progress edits (the switch reseeds the address
    // in place, see NativeInvoiceFormWorkspace.handleSelectFromEntity).
    const isAddressResolved =
        entityAddressQuery.isFetched || entityAddressQuery.isError;
    const [hasCreateWorkspaceOpened, setHasCreateWorkspaceOpened] =
        useState(false);
    const templateDetail = templateQuery.data ?? undefined;
    // The workspace hydrates the form once per mount, so it must not start from
    // a cached copy that is still being refetched (React Query renders cached
    // data first even at staleTime 0): the drawer would seed the form with
    // pre-save content and the next save would write it back over the edit.
    // Latched like the create gate so a later background refetch cannot unmount
    // the workspace and wipe in-progress edits.
    const [hasTemplateWorkspaceOpened, setHasTemplateWorkspaceOpened] =
        useState(false);
    const isTemplateReady =
        Boolean(templateRef) &&
        templateQuery.isSuccess &&
        templateDetail != null &&
        (hasTemplateWorkspaceOpened ||
            (!templateQuery.isFetching && areLedgersResolved));
    if (!hasTemplateWorkspaceOpened && isTemplateReady) {
        setHasTemplateWorkspaceOpened(true);
    }
    const isCreateReady =
        selectedEntity != null &&
        (templateRef
            ? isTemplateReady
            : hasCreateWorkspaceOpened ||
              (isAddressResolved && areLedgersResolved));
    if (!isEdit && !hasCreateWorkspaceOpened && isCreateReady) {
        setHasCreateWorkspaceOpened(true);
    }
    // Same staleness guard as the template gate above: an edit session must not
    // hydrate from a cached preview that is still refetching, or the form starts
    // from the content that preceded the last save.
    const [hasEditWorkspaceOpened, setHasEditWorkspaceOpened] = useState(false);
    const isEditReady =
        selectedEntity != null &&
        existingInvoiceEntityId !== null &&
        invoiceQuery.isSuccess &&
        previewQuery.isSuccess &&
        previewQuery.data?.renderSnapshot != null &&
        (hasEditWorkspaceOpened ||
            (!previewQuery.isFetching && areLedgersResolved));
    if (isEdit && !hasEditWorkspaceOpened && isEditReady) {
        setHasEditWorkspaceOpened(true);
    }
    const isEditFailed =
        isEdit &&
        (invoiceQuery.isError ||
            previewQuery.isError ||
            (previewQuery.isSuccess &&
                previewQuery.data?.renderSnapshot == null));
    const isFormReady = resolveIsFormReady({
        isEdit,
        isEditReady,
        templateRef,
        isTemplateReady,
        selectedEntity,
        isCreateReady,
    });
    const isTemplateFailed =
        Boolean(templateRef) &&
        (templateQuery.isError ||
            (templateQuery.isSuccess && templateQuery.data == null));
    const hasNoBusinessEntities = !isEdit && entities.length === 0;

    // Session remount is owned by NativeInvoiceDrawerWrapper's key (invoice id /
    // "new"). Do NOT key the workspace on entity id — entity switches keep
    // lines/customer and reseed via the from handler.
    if (isFormReady && selectedEntity) {
        const liveSuggestion = isEdit
            ? (previewQuery.data?.renderSnapshot?.invoiceNumber ?? "")
            : (suggestedInvoiceNumber ?? "");

        return (
            <NativeInvoiceFormWorkspace
                open={!!open}
                onClose={onClose}
                workspaceId={workspaceId ?? undefined}
                initialInvoiceId={initialInvoiceId}
                entities={entities}
                selectedEntity={selectedEntity}
                onSelectEntityId={setUserSelectedEntityId}
                suggestedInvoiceNumber={liveSuggestion}
                currentUserName={currentUser?.name}
                currentUserEmail={currentUser?.email}
                initialRenderSnapshot={previewQuery.data?.renderSnapshot}
                initialCustomer={initialCustomer}
                initialLogoUrl={previewQuery.data?.logoUrl}
                initialSendingEmailId={previewQuery.data?.sendingEmailId}
                initialCollectPayment={previewQuery.data?.collectPayment}
                initialPaymentToken={previewQuery.data?.paymentToken}
                initialStripeFinancialAccountId={
                    previewQuery.data?.stripeFinancialAccountId
                }
                initialAcceptedPaymentMethods={
                    previewQuery.data?.acceptedPaymentMethods
                }
                initialEmailSnapshot={previewQuery.data?.emailSnapshot}
                initialTermType={previewQuery.data?.termType}
                initialCustomTerm={previewQuery.data?.customTerm}
                initialIsEmailSubjectAutomatic={
                    previewQuery.data?.isEmailSubjectAutomatic
                }
                initialIsEmailMessageAutomatic={
                    previewQuery.data?.isEmailMessageAutomatic
                }
                initialLineCodingByLineId={initialLineCodingByLineId}
                initialAdjustmentSettings={
                    previewQuery.data?.adjustmentSettings
                }
                primaryAccrualLedgerId={primaryAccrualLedgerId}
                incomeAccountLedgerId={incomeAccountLedgerId}
                invoiceStatus={existingInvoice?.status ?? InvoiceStatus.DRAFT}
                createPrefill={
                    isEdit || templateRef ? undefined : createPrefill
                }
                entityAddress={entityAddressQuery.data}
                drawerMode={drawerMode}
                templateDetail={templateDetail}
                sourceTemplateId={applyTemplate?.templateId}
                templateId={editTemplate?.templateId}
                templateName={templateDetail?.name}
                onTemplateSaved={onTemplateSaved}
                isStacked={isStacked}
                onAnimationEnd={onAnimationEnd}
            />
        );
    }

    let loadingMessage: React.ReactNode = <Loader />;
    if (isEditFailed) {
        loadingMessage = "Couldn't load this invoice. Close and try again.";
    } else if (isTemplateFailed) {
        loadingMessage = "Couldn't load this template. Close and try again.";
    } else if (hasNoBusinessEntities) {
        loadingMessage = "Add a business entity before creating an invoice.";
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            onAnimationEnd={onAnimationEnd}
            size="fullScreen"
            noPadding
            hasBackdrop={isStacked}
            headerClassName={styles.flatHeader}
            headerAddonLeft={
                <NativeInvoiceDraftHeader
                    mode={drawerMode}
                    templateName={templateDetail?.name}
                />
            }
        >
            <div className={styles.workspace}>
                <div className={styles.loadingState}>{loadingMessage}</div>
            </div>
        </Drawer>
    );
};
